import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import {
  Avatar,
  Button,
  Card,
  CheckCard,
  ClubAvatar,
  CostTotal,
  DeadlineSetupRow,
  DepositAuthorize,
  DocChip,
  DocumentRow,
  Dropdown,
  DropdownItem,
  DropdownSeparator,
  Field,
  Icon,
  ICONS,
  IconButton,
  ImgPlaceholder,
  Kpi,
  LogoIcon,
  Modal,
  OptionList,
  OptionRow,
  PasswordInput,
  PasswordStrength,
  ProgressBar,
  Segmented,
  Select,
  StatusBadge,
  Stepper,
  Switch,
  Tabs,
  Tag,
  TextInput,
  Textarea,
  Timeline,
  Toast,
  VisibilityTag,
  type BadgeStatus,
  type IconName,
  type TimelineItem,
} from "@/components/ui";

/* -------------------------------------------------------------------------- */
/* Doc helpers                                                                */
/* -------------------------------------------------------------------------- */

function Code({ children }: { children: string }) {
  return (
    <pre className="kit-code">
      <code>{children}</code>
    </pre>
  );
}

function Spec({
  name,
  tag,
  desc,
  demo,
  code,
  demoClass,
}: {
  name: string;
  tag?: string;
  desc?: string;
  demo: ReactNode;
  code?: string;
  demoClass?: string;
}) {
  return (
    <div className="spec">
      <div className="spec-head">
        <h3>{name}</h3>
        {tag && <span className="spec-tag">{tag}</span>}
      </div>
      {desc && <p className="spec-desc">{desc}</p>}
      <div className={demoClass ? `spec-demo ${demoClass}` : "spec-demo"}>{demo}</div>
      {code && <Code>{code}</Code>}
    </div>
  );
}

function KitSection({
  id,
  title,
  sub,
  children,
}: {
  id: string;
  title: string;
  sub?: string;
  children: ReactNode;
}) {
  return (
    <section className="kit-section" id={id}>
      <div className="kit-section-head">
        <h2>{title}</h2>
        {sub && <p className="s-sub">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

function KitNote({ children }: { children: ReactNode }) {
  return (
    <div className="kit-note">
      <Icon name="sparkle" size={16} className="kn-ic" />
      <div>{children}</div>
    </div>
  );
}

function Token({
  color,
  name,
  value,
  use,
}: {
  color: string;
  name: string;
  value?: string;
  use?: string;
}) {
  return (
    <div className="token">
      <div className="swatch" style={{ background: color }} />
      <div className="tk-meta">
        <div className="tk-name">{name}</div>
        {value && <span className="tk-val">{value}</span>}
        {use && <span className="tk-use">{use}</span>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Foundation data (the real Ink & Gold tokens)                               */
/* -------------------------------------------------------------------------- */

const NEUTRALS: [string, string, string][] = [
  ["--bg", "oklch(0.985 0.005 85)", "App background"],
  ["--surface", "#ffffff", "Cards, inputs, sheets"],
  ["--surface-2", "oklch(0.976 0.006 84)", "Inset / hover fills"],
  ["--surface-3", "oklch(0.963 0.009 82)", "Tags, steppers"],
  ["--border", "oklch(0.92 0.009 80)", "Hairlines, dividers"],
  ["--border-strong", "oklch(0.85 0.014 78)", "Input & button borders"],
  ["--text", "oklch(0.26 0.014 68)", "Primary text, headings"],
  ["--text-muted", "oklch(0.5 0.016 72)", "Secondary text"],
  ["--text-faint", "oklch(0.63 0.014 76)", "Captions, placeholders"],
];
const BRAND: [string, string, string][] = [
  ["--ink", "oklch(0.25 0.018 64)", "Primary buttons, totals"],
  ["--ink-hover", "oklch(0.17 0.018 64)", "Primary button hover"],
  ["--primary", "oklch(0.485 0.082 77)", "Brand gold — links, active"],
  ["--primary-hover", "oklch(0.42 0.078 75)", "Gold hover"],
  ["--primary-soft", "oklch(0.948 0.034 84)", "Active nav, soft fills"],
  ["--gold", "#b3924f", "Logo mark, decorative"],
];
const STATUS: [string, string, string, string][] = [
  ["--success", "oklch(0.56 0.115 158)", "--success-soft", "Confirmed, done"],
  ["--warning", "oklch(0.66 0.13 70)", "--warning-soft", "Drafts, attention"],
  ["--danger", "oklch(0.57 0.165 27)", "--danger-soft", "Errors, destructive"],
  ["--info", "oklch(0.56 0.11 235)", "--info-soft", "Accepted, neutral info"],
  ["--slate", "oklch(0.55 0.02 258)", "--slate-soft", "Enquiry, completed"],
];

const TYPE_SCALE: [string, string, CSSProperties, string?][] = [
  ["Display", "38 / -0.04em / 700", { fontSize: 38, letterSpacing: "-0.04em", fontWeight: 700, lineHeight: 1.05 }, "Kit hero only"],
  ["Page title — h1", "26 / -0.025em / 650", { fontSize: 26, letterSpacing: "-0.025em", fontWeight: 650 }, ".page-head h1"],
  ["Section — h2", "24 / -0.03em / 650", { fontSize: 24, letterSpacing: "-0.03em", fontWeight: 650 }],
  ["Card title", "16 / -0.02em / 650", { fontSize: 16, letterSpacing: "-0.02em", fontWeight: 650 }, ".card-title"],
  ["Body", "15 / -0.01em / 400", { fontSize: 15, letterSpacing: "-0.01em", fontWeight: 400, lineHeight: 1.5 }, "body default"],
  ["Label", "13 / 600", { fontSize: 13, fontWeight: 600 }, ".field-label"],
  ["Caption", "12 / 500", { fontSize: 12, fontWeight: 500, color: "var(--text-faint)" }],
  ["Overline", "11 / 0.05em / 700", { fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700, color: "var(--text-faint)" }, ".nav-label, th"],
];

const RADII: [string, string][] = [
  ["--r-sm", "7px"],
  ["--r", "11px"],
  ["--r-lg", "16px"],
  ["--r-xl", "22px"],
];
const SHADOWS: [string, string][] = [
  ["--sh-sm", "buttons, cards at rest"],
  ["--sh", "hover lift"],
  ["--sh-lg", "modals, dropdowns, toasts"],
];
const SPACING: [string, string, string][] = [
  ["--gap", "22px", "Grid & stack gap (16px when .dense)"],
  ["--pad", "22px", "Card & page padding (16px when .dense)"],
  ["--sidebar-w", "248px", "Sidebar width"],
  ["--maxw", "1180px", "Page max-width"],
];

const STATUSES: { status: BadgeStatus; label: string }[] = [
  { status: "enquiry", label: "Enquiry" },
  { status: "proposal", label: "Proposal sent" },
  { status: "accepted", label: "Accepted" },
  { status: "completed", label: "Completed" },
  { status: "cancelled", label: "Cancelled" },
  { status: "postponed", label: "Postponed" },
];

const NAV: { group: string; links: { id: string; label: string }[] }[] = [
  {
    group: "Foundations",
    links: [
      { id: "colors", label: "Color" },
      { id: "type", label: "Typography" },
      { id: "tokens", label: "Radius · shadow · spacing" },
      { id: "icons", label: "Icons" },
    ],
  },
  {
    group: "Components",
    links: [
      { id: "buttons", label: "Buttons" },
      { id: "badges", label: "Badges & tags" },
      { id: "forms", label: "Form controls" },
      { id: "cards", label: "Cards & KPIs" },
      { id: "data", label: "Navigation & data" },
      { id: "feedback", label: "Feedback & messaging" },
      { id: "proposals", label: "Proposals" },
      { id: "documents", label: "Documents" },
      { id: "settings", label: "Settings & account" },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Foundation sections                                                        */
/* -------------------------------------------------------------------------- */

function ColorsSection() {
  return (
    <KitSection
      id="colors"
      title="Color"
      sub="Warm-tinted neutrals with a single deep-gold brand accent and near-black ink for primary actions. Status colors share one chroma family. Always reference tokens — never hard-code hex."
    >
      <Spec
        name="Neutrals"
        tag="surfaces & text"
        demoClass="plain col start"
        demo={
          <div className="token-grid">
            {NEUTRALS.map(([n, v, u]) => (
              <Token key={n} color={`var(${n})`} name={n} value={v} use={u} />
            ))}
          </div>
        }
      />
      <Spec
        name="Brand"
        tag="ink + gold"
        demoClass="plain col start"
        demo={
          <div className="token-grid">
            {BRAND.map(([n, v, u]) => (
              <Token key={n} color={`var(${n})`} name={n} value={v} use={u} />
            ))}
          </div>
        }
      />
      <Spec
        name="Status"
        tag="solid + soft pairs"
        desc="Each status has a solid (text / dot) and a -soft (background) variant. Used by badges, deadline nodes, and document chips."
        demoClass="plain col start"
        demo={
          <div className="token-grid">
            {STATUS.map(([n, v, soft, u]) => (
              <div className="token" key={n}>
                <div className="swatch" style={{ background: `var(${n})`, position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      inset: "auto 0 0 0",
                      height: "42%",
                      background: `var(${soft})`,
                      borderRadius: "0 0 9px 9px",
                    }}
                  />
                </div>
                <div className="tk-meta">
                  <div className="tk-name">{n}</div>
                  <span className="tk-val">{v}</span>
                  <span className="tk-use">{u}</span>
                </div>
              </div>
            ))}
          </div>
        }
        code={`/* status badge pattern */\n.badge { color: var(--success); background: var(--success-soft); }`}
      />
    </KitSection>
  );
}

function TypeSection() {
  return (
    <KitSection
      id="type"
      title="Typography"
      sub="Hanken Grotesk for everything; Geist Mono for numbers, codes, IDs and money. Headings run tight (negative tracking); body is -0.01em."
    >
      <Spec
        name="Families"
        tag="2 families"
        demoClass="col start plain"
        demo={
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, width: "100%" }}>
            <div style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 11, background: "var(--surface)" }}>
              <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.03em" }}>Aa</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text-faint)", marginTop: 8 }}>--font</div>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Hanken Grotesk</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>400 · 500 · 550 · 600 · 650 · 700</div>
            </div>
            <div style={{ padding: 16, border: "1px solid var(--border)", borderRadius: 11, background: "var(--surface)" }}>
              <div className="mono" style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-0.03em" }}>€1.2k</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text-faint)", marginTop: 8 }}>--mono</div>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Geist Mono</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Prices, IDs, dates, counts</div>
            </div>
          </div>
        }
      />
      <Spec
        name="Scale"
        tag="px / tracking / weight"
        demoClass="col start plain"
        demo={
          <div style={{ width: "100%" }}>
            {TYPE_SCALE.map(([name, spec, style, cls]) => (
              <div className="type-row" key={name}>
                <div className="type-spec">
                  <div className="ts-name">{name}</div>
                  <div className="ts-val">
                    {spec}
                    {cls ? ` · ${cls}` : ""}
                  </div>
                </div>
                <div className="type-sample" style={style}>
                  The fixture is confirmed
                </div>
              </div>
            ))}
          </div>
        }
      />
    </KitSection>
  );
}

function TokensSection() {
  return (
    <KitSection
      id="tokens"
      title="Radius, shadow & spacing"
      sub="Four radii, three shadows, and a small spacing set that collapses under the .dense density modifier."
    >
      <Spec
        name="Corner radius"
        tag="4 steps"
        demoClass="plain start"
        demo={
          <div className="tile-grid">
            {RADII.map(([n, v]) => (
              <div className="tile" key={n}>
                <div
                  className="tile-box"
                  style={{ borderRadius: `var(${n})`, background: "var(--primary-soft)", borderColor: "var(--primary)" }}
                />
                <div className="tile-cap">
                  <b>{n}</b>
                  <span>{v}</span>
                </div>
              </div>
            ))}
          </div>
        }
      />
      <Spec
        name="Elevation"
        tag="3 shadows"
        demoClass="plain start"
        demo={
          <div className="tile-grid">
            {SHADOWS.map(([n, u]) => (
              <div className="tile" key={n}>
                <div className="tile-box shadowed" style={{ boxShadow: `var(${n})`, borderRadius: "var(--r)" }} />
                <div className="tile-cap">
                  <b>{n}</b>
                  <span>{u}</span>
                </div>
              </div>
            ))}
          </div>
        }
      />
      <Spec
        name="Spacing & metrics"
        tag="layout vars"
        demoClass="plain col start"
        demo={
          <div className="token-grid">
            {SPACING.map(([n, v, u]) => (
              <div className="token" key={n}>
                <div
                  className="swatch"
                  style={{
                    background: "var(--surface-3)",
                    display: "grid",
                    placeItems: "center",
                    fontFamily: "var(--mono)",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--text-muted)",
                  }}
                >
                  {v.replace("px", "")}
                </div>
                <div className="tk-meta">
                  <div className="tk-name">{n}</div>
                  <span className="tk-use">{u}</span>
                </div>
              </div>
            ))}
          </div>
        }
      />
    </KitSection>
  );
}

function IconsSection() {
  const names = Object.keys(ICONS) as IconName[];
  return (
    <KitSection
      id="icons"
      title="Icons"
      sub="One stroke set — 24×24 viewBox, 1.8 stroke, round caps/joins. Rendered via <Icon name size stroke />. Size 18 in nav, 16 inside inputs, 15 in small buttons."
    >
      <Spec
        name={`Icon set · ${names.length}`}
        tag="<Icon name=…/>"
        demoClass="plain start"
        demo={
          <div className="icon-grid">
            {names.map((n) => (
              <div className="icon-cell" key={n}>
                <Icon name={n} size={22} />
                <span>{n}</span>
              </div>
            ))}
          </div>
        }
        code={`<Icon name="flight" size={18} stroke={1.8} />`}
      />
    </KitSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Component sections                                                         */
/* -------------------------------------------------------------------------- */

function ButtonsSection() {
  return (
    <KitSection
      id="buttons"
      title="Buttons"
      sub="Ink is the single primary action per view. Secondary is the bordered default; ghost for low-emphasis; soft (gold tint) for inline affirmatives; danger for destructive. Icons sit 8px before the label."
    >
      <Spec
        name="Variants"
        tag=".btn .btn-{variant}"
        demo={
          <>
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="soft">Soft</Button>
            <Button variant="danger">Danger</Button>
          </>
        }
        code={`<button class="btn btn-primary">Primary</button>\n<button class="btn btn-secondary">Secondary</button>\n<button class="btn btn-ghost">Ghost</button>\n<button class="btn btn-soft">Soft</button>\n<button class="btn btn-danger">Danger</button>`}
      />
      <Spec
        name="Sizes & states"
        tag=".btn-sm / -lg / :disabled"
        demo={
          <>
            <Button variant="primary" size="sm" icon="plus">
              Small
            </Button>
            <Button variant="primary" icon="plus">
              Medium
            </Button>
            <Button variant="primary" size="lg" icon="plus">
              Large
            </Button>
            <Button variant="secondary" iconRight="arrowR">
              With icon
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
          </>
        }
        code={`<button class="btn btn-primary btn-sm">…</button>   /* 6×11, 13px */\n<button class="btn btn-primary">…</button>          /* 9×15, 14px */\n<button class="btn btn-primary btn-lg">…</button>   /* 12×22, 15px */\n<button class="btn btn-primary btn-full">…</button> /* width:100% */`}
      />
      <Spec
        name="Icon buttons"
        tag=".icon-btn"
        desc="38×38 square targets for topbar / toolbar actions. Add .active for a gold state; .icon-badge for a count."
        demo={
          <>
            <IconButton name="bell" label="Notifications" badge={3} />
            <IconButton name="search" label="Search" />
            <IconButton name="settings" label="Settings" active />
            <IconButton name="dots" label="More" />
          </>
        }
        code={`<button class="icon-btn"><svg…/><span class="icon-badge">3</span></button>\n<button class="icon-btn active">…</button>`}
      />
    </KitSection>
  );
}

function BadgesSection() {
  return (
    <KitSection
      id="badges"
      title="Badges, tags & avatars"
      sub="Status uses the dot-badge; pills carry an icon + neutral fill; tags are tiny squared labels. Avatars are gradient-ink initials."
    >
      <Spec
        name="Status badge"
        tag="<StatusBadge status=…/>"
        desc="Colored dot + label; pulls the matching status color/soft pair from STATUS_META."
        demo={
          <>
            {STATUSES.map((s) => (
              <StatusBadge key={s.status} status={s.status} label={s.label} />
            ))}
          </>
        }
        code={`<span class="badge" style="color:var(--success);background:var(--success-soft)">\n  <span class="badge-dot" style="background:var(--success)"></span> Confirmed\n</span>`}
      />
      <Spec
        name="Pills, tags & flags"
        tag=".pill / .tag / .flag"
        demo={
          <>
            <span className="pill">
              <Icon name="flight" size={14} />
              Flight
            </span>
            <span className="pill">
              <Icon name="bed" size={14} />2 nights
            </span>
            <span className="tag">U18</span>
            <span className="tag">Hockey</span>
            <span className="flag">CAPTAIN</span>
            <DocChip status="ok" label="Passport" icon="check" />
            <DocChip status="warn" label="Expiring" icon="alert" />
            <DocChip status="missing" label="Missing" />
          </>
        }
        code={`<span class="pill"><svg…/> Flight</span>\n<span class="tag">U18</span>\n<span class="flag">CAPTAIN</span>\n<span class="doc-chip doc-ok">…</span>  <!-- doc-ok | doc-warn | doc-missing -->`}
      />
      <Spec
        name="Tag"
        tag="<Tag variant dot shape choice/>"
        desc="The one semantic pill for every soft-coloured status label. The .badge shape takes a colour variant (and an optional leading dot) for club/user status and counts; the .choice shape draws the fixed-palette 1st/2nd-preference chips and the ops “new” marker. Replaces hand-rolled style={{ color, background }} pills."
        demo={
          <>
            <Tag variant="primary">12</Tag>
            <Tag variant="success" dot>
              Active
            </Tag>
            <Tag variant="warning" dot>
              Suspended
            </Tag>
            <Tag variant="danger" dot>
              Disabled
            </Tag>
            <Tag variant="neutral">Archived</Tag>
            <Tag shape="choice" choice={1}>
              1st choice
            </Tag>
            <Tag shape="choice" choice={2}>
              2nd choice
            </Tag>
            <Tag shape="choice" choice={1}>
              New
            </Tag>
          </>
        }
        code={`<Tag variant="success" dot>Active</Tag>     {/* badge + leading dot */}\n<Tag variant="primary">12</Tag>             {/* count pill, no dot */}\n<Tag shape="choice" choice={1}>1st choice</Tag>\n<Tag shape="choice" choice={2}>2nd choice</Tag>`}
      />
      <Spec
        name="Avatars"
        tag="<Avatar initials src size/>"
        desc="Gradient-ink initials by default; pass src to show an uploaded profile photo (it falls back to initials when absent)."
        demo={
          <>
            <Avatar initials="MJ" size={28} />
            <Avatar initials="TC" size={36} />
            <Avatar initials="CF" size={44} />
            <Avatar
              initials="MV"
              size={44}
              src="data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='88'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23b3924f'/%3E%3Cstop offset='1' stop-color='%23715f33'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='88' height='88' fill='url(%23g)'/%3E%3C/svg%3E"
            />
          </>
        }
        code={`<div class="avatar" style="width:36px;height:36px">MJ</div>      <!-- gradient ink -->\n<div class="avatar avatar-img"><img src="…"></div>            <!-- photo -->`}
      />
      <Spec
        name="Club avatars"
        tag="<ClubAvatar name seed lg/>"
        desc="A club's identity chip: a rounded square in a colour derived deterministically from the seed (pass the club id so it survives renames), with the club's initials. Use it wherever a club is named — list, switcher, threads."
        demo={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ClubAvatar name="Atlètic Terrassa" seed="atletic-terrassa" />
            <ClubAvatar name="RC Polo Barcelona" seed="rc-polo" />
            <ClubAvatar name="CN Sabadell" seed="cn-sabadell" />
            <ClubAvatar name="RC Jolaseta" seed="jolaseta" lg />
          </div>
        }
        code={`<ClubAvatar name="RC Polo Barcelona" seed={club.id} />\n<ClubAvatar name="CN Sabadell" seed={club.id} lg />`}
      />
    </KitSection>
  );
}

function FormsSection() {
  const [seg, setSeg] = useState("flight");
  const [pax, setPax] = useState(18);
  const [sw, setSw] = useState(true);
  const [hb, setHb] = useState(true);
  const [fb, setFb] = useState(false);
  const [remember, setRemember] = useState(true);

  return (
    <KitSection
      id="forms"
      title="Form controls"
      sub="Inputs are 1px border-strong, 7px radius, with a gold focus ring (3px primary-soft). Labels are 13px / 600. Errors swap the ring to danger."
    >
      <Spec
        name="Text fields"
        tag=".field · .input"
        desc="Wrap label + control in .field. Use .input-wrap.has-icon to inset a 16px leading icon."
        demoClass="col start"
        demo={
          <div className="form-grid" style={{ width: "100%" }}>
            <Field label="Full name">
              <TextInput placeholder="Marc Joval" />
            </Field>
            <Field label="Email" hint="We'll only use this for trip updates">
              <TextInput icon="mail" type="email" placeholder="you@club.com" />
            </Field>
            <Field label="Notes" full>
              <Textarea rows={2} placeholder="Anything else we should know?" />
            </Field>
          </div>
        }
        code={`<label class="field">\n  <span class="field-label">Email</span>\n  <div class="input-wrap has-icon">\n    <svg class="input-icon"…/>\n    <input class="input" placeholder="you@club.com">\n  </div>\n</label>`}
      />
      <Spec
        name="States"
        tag=":focus · .input-error"
        demoClass="start"
        demo={
          <div className="form-grid" style={{ width: "100%" }}>
            <Field label="Default">
              <input className="input" defaultValue="Resting" />
            </Field>
            <Field label="Focused">
              <input
                className="input"
                defaultValue="Gold focus ring"
                style={{ borderColor: "var(--primary)", boxShadow: "0 0 0 3px var(--primary-soft)" }}
              />
            </Field>
            <Field label="Error">
              <input className="input input-error" defaultValue="Invalid value" />
            </Field>
            <Field label="Select">
              <Select
                value="hb"
                onChange={() => {}}
                options={[
                  { value: "hb", label: "Half board" },
                  { value: "fb", label: "Full board" },
                ]}
              />
            </Field>
          </div>
        }
      />
      <Spec
        name="Choice controls"
        tag="check-card · segmented"
        demoClass="col start"
        demo={
          <>
            <div className="demo-label">Check cards</div>
            <div className="check-grid" style={{ width: "100%" }}>
              <CheckCard checked={hb} onChange={setHb} icon="bed" label="Half board" sub="Breakfast + dinner" />
              <CheckCard checked={fb} onChange={setFb} icon="utensils" label="Full board" sub="All meals included" />
            </div>
            <div className="demo-label">Segmented</div>
            <Segmented
              value={seg}
              onChange={setSeg}
              options={[
                { value: "flight", label: "Flight", icon: "flight" },
                { value: "train", label: "Train", icon: "train" },
                { value: "coach", label: "Coach", icon: "coach" },
              ]}
            />
          </>
        }
        code={`<button class="check-card checked"><span class="check-box">✓</span> …</button>\n<div class="segmented"><button class="seg-item active">Flight</button>…</div>`}
      />
      <Spec
        name="Stepper · switch · checkbox"
        tag="stepper · switch · rm-box"
        demo={
          <>
            <Stepper value={pax} onChange={setPax} label="pax" min={1} max={60} />
            <Switch checked={sw} onChange={setSw} label="Toggle" />
            <button type="button" className="remember" onClick={() => setRemember((r) => !r)}>
              <span className={remember ? "rm-box on" : "rm-box"}>
                {remember && <Icon name="check" size={12} stroke={2.6} />}
              </span>
              Remember me
            </button>
          </>
        }
        code={`<div class="stepper"><button>−</button><span class="stepper-val">18</span><button>+</button></div>\n<button class="switch on"><i></i></button>\n<button class="remember"><span class="rm-box on">✓</span> Remember me</button>`}
      />
      <Spec
        name="Search & 2FA"
        tag="topbar search · code-box"
        demoClass="start"
        demo={
          <>
            <div className="input-wrap has-icon" style={{ maxWidth: 280 }}>
              <Icon name="search" size={16} className="input-icon" />
              <input
                className="input"
                placeholder="Search trips, hotels…"
                style={{ background: "var(--surface-2)", borderColor: "transparent" }}
              />
            </div>
            <div className="code-row" style={{ maxWidth: 230 }}>
              {["2", "4", "9", "", "", ""].map((d, i) => (
                <input key={i} className="input code-box" defaultValue={d} maxLength={1} />
              ))}
            </div>
          </>
        }
      />
    </KitSection>
  );
}

function CardsSection() {
  const kpis: [IconName, string, string, string][] = [
    ["trips", "Active trips", "12", "+3 this month"],
    ["roster", "Travelling players", "248", "across 12 trips"],
    ["calendar", "Deadlines this week", "5", "2 need action"],
    ["invoices", "Outstanding", "€18.4k", "3 invoices"],
  ];
  return (
    <KitSection
      id="cards"
      title="Cards & KPIs"
      sub="Cards are surface + 16px radius + sh-sm. Add .card-hover for clickable lift. KPIs pair a soft-gold icon tile with a tight-tracked metric."
    >
      <Spec
        name="Card"
        tag=".card .card-pad"
        demoClass="start"
        demo={
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, width: "100%" }}>
            <Card>
              <div className="card-head">
                <span className="card-title">Static card</span>
                <Icon name="dots" size={16} />
              </div>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                Resting elevation with sh-sm and a hairline border.
              </p>
            </Card>
            <Card hover>
              <div className="card-head">
                <span className="card-title">Hover card</span>
                <Icon name="arrowR" size={16} />
              </div>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                Lifts on hover — use for navigable rows and tiles.
              </p>
            </Card>
          </div>
        }
        code={`<div class="card card-pad">…</div>\n<div class="card card-pad card-hover">…</div>  <!-- clickable -->`}
      />
      <Spec
        name="KPI"
        tag="<Kpi icon value label sub/>"
        desc="A metric tile: a soft-gold icon chip + label, a tight-tracked value, and a faint caption. Drop one inside a Card; wrap the cards in a .kpi-grid for the dashboard layout."
        demoClass="start"
        demo={
          <div className="kpi-grid" style={{ width: "100%", marginBottom: 0 }}>
            {kpis.map(([ic, lbl, val, sub]) => (
              <Card key={lbl}>
                <Kpi icon={ic} label={lbl} value={val} sub={sub} />
              </Card>
            ))}
          </div>
        }
        code={`<Card>\n  <Kpi icon="trips" label="Active trips" value="12" sub="+3 this month" />\n</Card>`}
      />
    </KitSection>
  );
}

function DataSection() {
  const [tab, setTab] = useState("open");
  return (
    <KitSection
      id="data"
      title="Navigation & data"
      sub="Sidebar nav items, tabs, tables and the deadline timeline. Active states are always gold-soft fill + gold text."
    >
      <Spec
        name="Nav items"
        tag=".nav-item"
        demoClass="col start plain"
        demo={
          <div style={{ width: 240, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 11, padding: 10 }}>
            <button className="nav-item active">
              <Icon name="dashboard" size={18} />
              <span>Dashboard</span>
            </button>
            <button className="nav-item">
              <Icon name="trips" size={18} />
              <span>Trips</span>
              <span className="nav-count">4</span>
            </button>
            <button className="nav-item">
              <Icon name="messages" size={18} />
              <span>Messages</span>
              <span className="nav-count">2</span>
            </button>
            <button className="nav-item">
              <Icon name="invoices" size={18} />
              <span>Invoices</span>
            </button>
          </div>
        }
        code={`<button class="nav-item active"><svg…/> Dashboard</button>\n<button class="nav-item"><svg…/> Trips <span class="nav-count">4</span></button>`}
      />
      <Spec
        name="Tabs"
        tag=".tabs .tab"
        demoClass="col start"
        demo={
          <Tabs
            value={tab}
            onChange={setTab}
            options={[
              { value: "open", label: "Open", count: 4 },
              { value: "sent", label: "Sent", count: 2 },
              { value: "done", label: "Confirmed" },
            ]}
          />
        }
        code={`<div class="tabs">\n  <button class="tab active">Open <span class="tab-count">4</span></button>\n  <button class="tab">Confirmed</button>\n</div>`}
      />
      <Spec
        name="Table"
        tag=".table"
        demoClass="col start plain"
        demo={
          <div style={{ width: "100%", overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Dates</th>
                  <th>Pax</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="t-trip">
                    <b>Lleida away</b>
                    <small>Hockey · U18</small>
                  </td>
                  <td className="mono">12–14 Mar</td>
                  <td className="mono">18</td>
                  <td>
                    <StatusBadge status="accepted" label="Accepted" />
                  </td>
                </tr>
                <tr>
                  <td className="t-trip">
                    <b>Valencia cup</b>
                    <small>Hockey · Senior</small>
                  </td>
                  <td className="mono">02–05 Apr</td>
                  <td className="mono">22</td>
                  <td>
                    <StatusBadge status="proposal" label="Proposal sent" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        }
        code={`<table class="table">\n  <thead><tr><th>Trip</th>…</tr></thead>   <!-- 11px uppercase faint -->\n  <tbody><tr><td>…</td></tr></tbody>     <!-- hover highlights row -->\n</table>`}
      />
      <Spec
        name="Timeline — deadline"
        tag={`<Timeline variant="deadline"/>`}
        desc="The proposal deadline rail. Each item carries a node glyph (numeral or check), a title, a date sub-line, a trailing chip, and a state: done (green node + line), overdue (danger), or pending (hollow). Nodes for completed steps tint their connector green."
        demoClass="col start"
        demo={
          <div style={{ width: "100%", maxWidth: 460 }}>
            <Timeline
              variant="deadline"
              items={
                [
                  {
                    key: "accept",
                    node: <Icon name="check" size={12} stroke={2.5} />,
                    title: "Acceptance deadline",
                    sub: "14 Mar 2026",
                    state: "done",
                    meta: <DocChip status="ok" label="Confirmed" icon="check" />,
                  },
                  {
                    key: "deposit",
                    node: 2,
                    title: "Deposit due",
                    sub: "21 Mar 2026",
                    state: "overdue",
                    meta: (
                      <span className="dlc-when" style={{ color: "var(--danger)" }}>
                        2 days overdue
                      </span>
                    ),
                  },
                  {
                    key: "paxlist",
                    node: 3,
                    title: "Passenger list",
                    sub: "04 Apr 2026",
                    state: "pending",
                    meta: <span className="dlc-when">in 8 days</span>,
                  },
                ] satisfies TimelineItem[]
              }
            />
          </div>
        }
        code={`<Timeline variant="deadline" items={deadlines.map((d, i) => ({\n  key: d.key,\n  node: d.done ? <Icon name="check" size={12} stroke={2.5} /> : i + 1,\n  title: t(d.labelKey),\n  sub: fmt.date(d.date),\n  state: d.done ? "done" : d.overdue ? "overdue" : "pending",\n  meta: <span className="dlc-when">…</span>,\n}))} />`}
      />
      <Spec
        name="Timeline — audit"
        tag={`<Timeline variant="audit"/>`}
        desc="The per-trip activity log. Bare rows (no wrapper) with an icon node, a bold action title, the actor as a sub-line, and a trailing timestamp. Audit nodes are unstyled by state."
        demoClass="col start plain"
        demo={
          <Card style={{ width: "100%" }}>
            <Timeline
              variant="audit"
              items={
                [
                  {
                    key: "a1",
                    node: <Icon name="check" size={13} />,
                    title: "Proposal accepted",
                    sub: "Marta Vidal",
                    meta: "10:31",
                  },
                  {
                    key: "a2",
                    node: <Icon name="send" size={13} />,
                    title: "Proposal sent",
                    sub: "Operations",
                    meta: "09:14",
                  },
                  {
                    key: "a3",
                    node: <Icon name="edit" size={13} />,
                    title: "Trip created",
                    sub: "System",
                    meta: "Yesterday",
                  },
                ] satisfies TimelineItem[]
              }
            />
          </Card>
        }
        code={`<Timeline variant="audit" items={entries.map((e) => ({\n  key: e.id,\n  node: <Icon name={icon} size={13} />,\n  title: t(labelKey, params),\n  sub: e.actorName ?? t("audit.system"),\n  meta: fmt.date(e.createdAt),\n}))} />`}
      />
      <Spec
        name="Progress bar"
        tag="<ProgressBar value variant/>"
        desc="A thin track-and-fill bar (used for manifest completion). The variant tints the fill — primary while in progress, success once complete."
        demoClass="col start"
        demo={
          <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 6 }}>
                Goalkeepers — 2 of 3
              </div>
              <ProgressBar value={67} variant="primary" />
            </div>
            <div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 6 }}>
                Defenders — 6 of 6
              </div>
              <ProgressBar value={100} variant="success" />
            </div>
          </div>
        }
        code={`<ProgressBar value={role.pct} variant={role.complete ? "success" : "primary"} />`}
      />
    </KitSection>
  );
}

function FeedbackSection() {
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(false);
  return (
    <KitSection
      id="feedback"
      title="Feedback & messaging"
      sub="Inline alerts, internal notes, toasts, empty states, modals and chat bubbles. Destructive/error always uses danger-soft; internal-only content uses the warm note style."
    >
      <Spec
        name="Alerts & notes"
        tag=".auth-alert · .note"
        demoClass="col start"
        demo={
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="auth-alert" style={{ margin: 0 }}>
              <Icon name="alert" size={17} className="aa-ic" />
              <div className="aa-body">
                <b>We couldn't sign you in.</b> The email or password is incorrect.
              </div>
            </div>
            <div className="note" style={{ margin: 0 }}>
              <Avatar initials="OP" size={32} />
              <div className="note-body">
                <div className="note-top">
                  <b>Operations</b>
                  <span className="internal-tag">Internal</span>
                </div>
                <div className="note-text">
                  Hold 4 twin rooms until the roster is confirmed — venue is tight that weekend.
                </div>
              </div>
            </div>
          </div>
        }
        code={`<div class="auth-alert"><svg class="aa-ic"…/><div class="aa-body">…</div></div>\n<div class="note">…<span class="internal-tag">Internal</span>…</div>`}
      />
      <Spec
        name="Toast · empty state"
        tag=".toast · .empty"
        demoClass="start"
        demo={
          <>
            <Button variant="secondary" icon="bell" onClick={() => {
              setToast(true);
              window.setTimeout(() => setToast(false), 2400);
            }}>
              Show toast
            </Button>
            <div className="empty" style={{ padding: "26px 30px", flex: 1, minWidth: 220 }}>
              <div className="empty-ic">
                <Icon name="messages" size={22} />
              </div>
              <div style={{ fontWeight: 600, color: "var(--text)" }}>No messages yet</div>
              <div style={{ fontSize: 13 }}>Start a conversation with TC.</div>
            </div>
          </>
        }
      />
      <Spec
        name="Chat bubbles"
        tag=".bubble.them / .me"
        demoClass="col start"
        demo={
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: "var(--surface-2)",
              padding: 16,
              borderRadius: 12,
              border: "1px solid var(--border)",
            }}
          >
            <div className="bubble them">
              Can we add a recovery session on day two?<div className="b-meta">TC · 10:24</div>
            </div>
            <div className="bubble me">
              Yes — I'll book the ice facilities and update the proposal.<div className="b-meta">You · 10:31</div>
            </div>
          </div>
        }
        code={`<div class="bubble them">…<div class="b-meta">TC · 10:24</div></div>\n<div class="bubble me">…</div>   <!-- gold, right-aligned -->`}
      />
      <Spec
        name="Invoice / document status"
        tag=".vis-tag · .doc-chip"
        desc="Invoice visibility (eye/eye-off) controls whether a club can see an invoice; document-readiness chips track roster passport/DNI status."
        demoClass="start"
        demo={
          <>
            <VisibilityTag visible shownLabel="Visible to club" hiddenLabel="Not visible to club" />
            <VisibilityTag visible={false} shownLabel="Visible to club" hiddenLabel="Not visible to club" />
            <DocChip status="ok" label="Verified" icon="check" />
            <DocChip status="warn" label="Expires soon" icon="alert" />
            <DocChip status="missing" label="Missing" />
          </>
        }
      />
      <Spec
        name="Dropdown menu"
        tag=".dropdown"
        demoClass="start"
        demo={
          <Dropdown
            align="left"
            trigger={
              <Button variant="secondary" iconRight="chevD">
                Club de Campo
              </Button>
            }
          >
            <DropdownItem label="All clubs" icon="globe" active />
            <DropdownItem label="RC Polo" icon="building" />
            <DropdownItem label="Club de Campo" icon="building" />
            <DropdownSeparator />
            <DropdownItem label="Manage clubs" icon="settings" />
          </Dropdown>
        }
      />
      <Spec
        name="Modal"
        tag="<Modal open title/>"
        desc="Center-scrim overlay with blur. 460px default width; header + .modal-body + .modal-foot (right-aligned actions)."
        demo={
          <>
            <Button variant="secondary" onClick={() => setModal(true)}>
              Open modal
            </Button>
            <Modal open={modal} onClose={() => setModal(false)} title="Confirm deposit paid">
              <div className="modal-body">
                <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                  By confirming, you acknowledge the deposit has been transferred to TC. No payment is
                  taken here.
                </p>
              </div>
              <div className="modal-foot">
                <Button variant="ghost" onClick={() => setModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => setModal(false)}>
                  Confirm
                </Button>
              </div>
            </Modal>
          </>
        }
        code={`<div class="modal-scrim"><div class="modal">\n  <div class="modal-head"><h3>…</h3><button class="icon-btn">✕</button></div>\n  <div class="modal-body">…</div>\n  <div class="modal-foot"><button class="btn btn-ghost">Cancel</button>…</div>\n</div></div>`}
      />
      <Spec
        name="Image placeholder"
        tag="<ImgPlaceholder label/>"
        desc="Striped placeholder for any imagery not yet supplied — label it with what belongs there."
        demoClass="start"
        demo={
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, width: "100%" }}>
            <ImgPlaceholder label="hotel exterior" h={110} />
            <ImgPlaceholder label="city / venue" h={110} />
          </div>
        }
        code={`<div class="img-ph" style="height:120px"><span>hotel exterior</span></div>`}
      />
      {toast && <Toast message="Request submitted" />}
    </KitSection>
  );
}

function SettingsSection() {
  const [navSel, setNavSel] = useState("profile");
  const [pw, setPw] = useState("trotamundos");
  const [twofa, setTwofa] = useState(true);
  const [notif, setNotif] = useState({ email: true, inapp: true });

  const setNav: { id: string; icon: IconName; label: string }[] = [
    { id: "profile", icon: "user", label: "Profile" },
    { id: "security", icon: "settings", label: "Security" },
    { id: "notifs", icon: "bell", label: "Notifications" },
    { id: "prefs", icon: "globe", label: "Preferences" },
  ];

  const notifRows: { key: string; label: string; sub: string }[] = [
    { key: "proposal", label: "Proposals", sub: "When TC sends or revises a proposal" },
    { key: "deadline", label: "Deadlines", sub: "Reminders as a deadline approaches" },
    { key: "message", label: "Messages", sub: "New messages on a trip or the inbox" },
  ];

  return (
    <KitSection
      id="settings"
      title="Settings & account"
      sub="The /settings screen: a sticky section sub-nav beside stacked cards. Reuse this vocabulary for Profile, Security, Notifications and Preferences — never re-style it per section."
    >
      <Spec
        name="Section sub-nav"
        tag=".set-nav · .set-nav-item"
        desc="Sticky left rail that switches the panel. Active item is gold-soft fill + gold text, matching .nav-item. Collapses to a scrollable row under 760px."
        demoClass="start"
        demo={
          <nav className="set-nav" style={{ position: "static", width: 212 }}>
            {setNav.map((n) => (
              <button
                key={n.id}
                className={navSel === n.id ? "set-nav-item active" : "set-nav-item"}
                onClick={() => setNavSel(n.id)}
              >
                <Icon name={n.icon} size={18} />
                <span>{n.label}</span>
              </button>
            ))}
          </nav>
        }
        code={`<nav class="set-nav">\n  <button class="set-nav-item active"><svg…/> Profile</button>\n  <button class="set-nav-item"><svg…/> Security</button>\n</nav>`}
      />
      <Spec
        name="Section header & save bar"
        tag=".set-section-head · .card-foot"
        desc="Every editable card opens with a section head (h2 + sub) and closes with a right-aligned save bar. The foot-note (left) flags unsaved changes; the primary save disables when the form is clean."
        demoClass="col start plain"
        demo={
          <Card>
            <div className="set-section-head" style={{ marginBottom: 18 }}>
              <h2>Personal information</h2>
              <div className="sub">Update how your name and contact details appear to TC.</div>
            </div>
            <div className="form-grid">
              <Field label="Full name">
                <TextInput icon="user" placeholder="Marta Vidal" />
              </Field>
              <Field label="Job title">
                <TextInput placeholder="Team Manager" />
              </Field>
            </div>
            <div className="card-foot">
              <span className="foot-note">
                <span className="badge-dot" style={{ background: "var(--warning)" }} />
                Unsaved changes
              </span>
              <Button variant="ghost">Cancel</Button>
              <Button variant="primary" icon="check">
                Save changes
              </Button>
            </div>
          </Card>
        }
        code={`<div class="set-section-head"><h2>…</h2><div class="sub">…</div></div>\n<div class="card-foot">\n  <span class="foot-note"><span class="badge-dot"></span> Unsaved changes</span>\n  <button class="btn btn-ghost">Cancel</button>\n  <button class="btn btn-primary">Save changes</button>\n</div>`}
      />
      <Spec
        name="Settings row"
        tag=".set-row · .sr-label / .sr-sub"
        desc="Label + sub-label on the left, a single control on the right, divided by hairlines. Used for preferences (language/timezone/density) and the 2FA toggle."
        demoClass="col start plain"
        demo={
          <Card>
            <div className="set-row">
              <div className="sr-text">
                <div className="sr-label">Two-factor authentication</div>
                <div className="sr-sub">Require a 6-digit code from your authenticator at sign-in.</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span
                  className="tag"
                  style={{
                    background: twofa ? "var(--success-soft)" : "var(--surface-3)",
                    color: twofa ? "var(--success)" : "var(--text-faint)",
                  }}
                >
                  {twofa ? "On" : "Off"}
                </span>
                <Switch checked={twofa} onChange={setTwofa} label="Two-factor authentication" />
              </div>
            </div>
            <div className="set-row">
              <div className="sr-text">
                <div className="sr-label">Language</div>
                <div className="sr-sub">Applies to the interface and to emails from TC.</div>
              </div>
              <Segmented
                size="sm"
                value="en"
                onChange={() => {}}
                options={[
                  { value: "en", label: "English" },
                  { value: "es", label: "Español" },
                ]}
              />
            </div>
          </Card>
        }
        code={`<div class="set-row">\n  <div class="sr-text"><div class="sr-label">…</div><div class="sr-sub">…</div></div>\n  <!-- control: switch, segmented or select -->\n</div>`}
      />
      <Spec
        name="Photo uploader"
        tag=".photo-row · .photo-lg"
        desc="An 88px round preview beside change/remove actions. The preview reuses the avatar; an uploaded image fills it via .photo-lg img."
        demoClass="start"
        demo={
          <div className="photo-row">
            <div className="photo-lg avatar" style={{ fontSize: 32 }}>
              MV
            </div>
            <div className="photo-actions">
              <div className="pa-btns">
                <Button variant="secondary" size="sm" icon="upload">
                  Upload photo
                </Button>
              </div>
              <span className="faint" style={{ fontSize: 12 }}>
                JPG or PNG, up to 2&nbsp;MB.
              </span>
            </div>
          </div>
        }
        code={`<div class="photo-row">\n  <div class="photo-lg avatar"><img src="…"></div>\n  <div class="photo-actions"><div class="pa-btns">…</div></div>\n</div>`}
      />
      <Spec
        name="Password field & strength"
        tag="<PasswordInput/> · <PasswordStrength/>"
        desc="Password input with a show/hide eye toggle, paired with the 4-segment strength meter (passwordStrength scores 0–4: weak…strong)."
        demoClass="col start"
        demo={
          <div className="form-grid" style={{ width: "100%" }}>
            <Field label="New password" full>
              <PasswordInput value={pw} onChange={setPw} ariaLabel="New password" autoComplete="new-password" />
              {pw && (
                <PasswordStrength
                  value={pw}
                  labels={["Weak", "Weak", "Fair", "Good", "Strong"]}
                />
              )}
            </Field>
          </div>
        }
        code={`<PasswordInput value={pw} onChange={setPw} autoComplete="new-password" />\n<PasswordStrength value={pw} labels={["Weak","Weak","Fair","Good","Strong"]} />`}
      />
      <Spec
        name="Notification matrix"
        tag=".notif-head · .notif-row"
        desc="A category × channel grid. Each row is one element with a continuous divider; the Email / In-app headers sit above 76px toggle columns."
        demoClass="col start plain"
        demo={
          <Card>
            <div className="notif-head">
              <div className="nh-spacer" />
              <div className="nh">Email</div>
              <div className="nh">In-app</div>
            </div>
            {notifRows.map((r) => (
              <div className="notif-row" key={r.key}>
                <div className="nr-text">
                  <div className="nr-label">{r.label}</div>
                  <div className="nr-sub">{r.sub}</div>
                </div>
                <div className="nr-toggles">
                  <div className="nr-cell">
                    <Switch
                      checked={notif.email}
                      onChange={(v) => setNotif((s) => ({ ...s, email: v }))}
                      label={`${r.label} email`}
                    />
                  </div>
                  <div className="nr-cell">
                    <Switch
                      checked={notif.inapp}
                      onChange={(v) => setNotif((s) => ({ ...s, inapp: v }))}
                      label={`${r.label} in-app`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </Card>
        }
        code={`<div class="notif-head"><div class="nh-spacer"></div><div class="nh">Email</div><div class="nh">In-app</div></div>\n<div class="notif-row">\n  <div class="nr-text"><div class="nr-label">Proposals</div><div class="nr-sub">…</div></div>\n  <div class="nr-toggles"><div class="nr-cell"><button class="switch on"><i></i></button></div>…</div>\n</div>`}
      />
      <Spec
        name="Active session row"
        tag=".session-row"
        desc="One row per signed-in device — icon tile, device + location, and a per-device sign-out. The current device is flagged and can't revoke itself."
        demoClass="col start plain"
        demo={
          <Card>
            <div className="session-row">
              <div className="session-ic">
                <Icon name="globe" size={18} />
              </div>
              <div className="session-body">
                <b>Chrome · macOS</b>
                <span className="tag" style={{ marginLeft: 8, background: "var(--primary-soft)", color: "var(--primary)" }}>
                  This device
                </span>
                <div className="se-meta">Barcelona, ES · Active now</div>
              </div>
            </div>
            <div className="session-row">
              <div className="session-ic">
                <Icon name="messages" size={18} />
              </div>
              <div className="session-body">
                <b>Safari · iPhone</b>
                <div className="se-meta">Barcelona, ES · 2h ago</div>
              </div>
              <Button variant="ghost" size="sm">
                Sign out
              </Button>
            </div>
          </Card>
        }
        code={`<div class="session-row">\n  <div class="session-ic"><svg…/></div>\n  <div class="session-body"><b>Chrome · macOS</b><div class="se-meta">…</div></div>\n  <button class="btn btn-ghost btn-sm">Sign out</button>\n</div>`}
      />
    </KitSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Proposals                                                                  */
/* -------------------------------------------------------------------------- */

const PROPOSAL_LINES = [
  { id: "fl", desc: "Return flights BCN ⇄ AMS", qty: "18 × €268", amt: 4824 },
  { id: "ht", desc: "Park Centraal · 9 twin · 3 nights · B&B", qty: "27 × €142", amt: 3834 },
  { id: "co", desc: "Private coach transfers", qty: "4 × €320", amt: 1280 },
];
const eur = (n: number) => `€${n.toLocaleString("en-US")}`;

const DEADLINE_SETUP: { key: string; label: string }[] = [
  { key: "acceptance", label: "Acceptance deadline" },
  { key: "deposit", label: "Deposit due" },
  { key: "paxlist", label: "Passenger list" },
];

function ProposalsSection() {
  const [selected, setSelected] = useState<Set<string>>(new Set(PROPOSAL_LINES.map((l) => l.id)));
  const [deposit, setDeposit] = useState(false);
  const [deadlines, setDeadlines] = useState<Record<string, { on: boolean; date: string }>>({
    acceptance: { on: true, date: "2026-03-14" },
    deposit: { on: true, date: "2026-03-21" },
    paxlist: { on: false, date: "" },
  });
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const total = PROPOSAL_LINES.filter((l) => selected.has(l.id)).reduce((s, l) => s + l.amt, 0);

  return (
    <KitSection
      id="proposals"
      title="Proposals"
      sub="A proposal is one working object. The back office prices it as line items; the club ticks the options it wants and the total recomputes live; a deposit-bearing trip gates acceptance behind an authorisation. Sent proposals lock until revised."
    >
      <Spec
        name="Option list — selectable"
        tag="<OptionList> · <OptionRow>"
        desc="The club's live picker while a proposal is Sent. Ticking includes a line; a deselected line is struck through and the total recomputes from the selection."
        demoClass="col start"
        demo={
          <div style={{ width: "100%" }}>
            <OptionList>
              {PROPOSAL_LINES.map((l) => (
                <OptionRow
                  key={l.id}
                  description={l.desc}
                  qtyLabel={l.qty}
                  amountLabel={eur(l.amt)}
                  selectable
                  checked={selected.has(l.id)}
                  onToggle={() => toggle(l.id)}
                />
              ))}
            </OptionList>
            <CostTotal
              label="Selected total"
              amountLabel={eur(total)}
              perPersonLabel={`${eur(Math.round(total / 18))} per person · 18 pax`}
            />
          </div>
        }
        code={`<OptionList>\n  <OptionRow description="Return flights BCN ⇄ AMS" qtyLabel="18 × €268"\n    amountLabel="€4,824" selectable checked={sel.has(id)} onToggle={…} />\n</OptionList>\n<CostTotal label="Selected total" amountLabel="€9,938"\n  perPersonLabel="€552 per person · 18 pax" />`}
      />
      <Spec
        name="Option list — read-only"
        tag=".opt-row.opt-static"
        desc="Once accepted, the surviving lines are the agreed scope — the list renders static (no checkboxes, no strike-through). Same component, `selectable` omitted."
        demoClass="col start"
        demo={
          <div style={{ width: "100%" }}>
            <OptionList>
              {PROPOSAL_LINES.map((l) => (
                <OptionRow key={l.id} description={l.desc} qtyLabel={l.qty} amountLabel={eur(l.amt)} />
              ))}
            </OptionList>
            <CostTotal
              label="Total"
              amountLabel={eur(9938)}
              perPersonLabel="€552 per person · 18 pax"
            />
          </div>
        }
      />
      <Spec
        name="Deposit authorisation"
        tag="<DepositAuthorize>"
        desc="Gated consent before a club accepts a deposit-bearing trip — the accept action stays disabled until it's ticked. A manual acknowledgement only; no payment is taken here. `disabled` locks the consent while accepting is blocked (e.g. a pending trip amendment)."
        demoClass="col start"
        demo={
          <div style={{ width: "100%", maxWidth: 420 }}>
            <DepositAuthorize
              checked={deposit}
              onChange={setDeposit}
              label="I authorise Travel Connection to pay the hotel deposit on our behalf to reserve the accommodation."
            />
            <Button variant="primary" full icon="check" disabled={!deposit} style={{ marginTop: 12 }}>
              Accept proposal
            </Button>
            <div style={{ marginTop: 16 }}>
              <DepositAuthorize checked={false} onChange={() => {}} disabled label="Disabled while the amended request awaits TC review." />
            </div>
          </div>
        }
        code={`<DepositAuthorize checked={ok} onChange={setOk} label="I authorise…" />\n<Button variant="primary" full icon="check" disabled={!ok}>Accept proposal</Button>\n<DepositAuthorize checked={false} onChange={noop} disabled label="…" />`}
      />
      <Spec
        name="Deadline setup row"
        tag="<DeadlineSetupRow>"
        desc="The back office builds the deadline schedule one row at a time: a switch turns each adaptive deadline on or off, the label names it, and the trailing date input is enabled only while the row is on. A switched-off row dims; pass disabled to lock the whole list once the proposal is sent."
        demoClass="col start"
        demo={
          <div style={{ width: "100%", maxWidth: 460 }}>
            {DEADLINE_SETUP.map((d) => (
              <DeadlineSetupRow
                key={d.key}
                label={d.label}
                enabled={deadlines[d.key]?.on ?? false}
                onToggle={(on) =>
                  setDeadlines((prev) => ({
                    ...prev,
                    [d.key]: { on, date: prev[d.key]?.date ?? "" },
                  }))
                }
                date={deadlines[d.key]?.date}
                onDateChange={(date) =>
                  setDeadlines((prev) => ({
                    ...prev,
                    [d.key]: { on: prev[d.key]?.on ?? false, date },
                  }))
                }
              />
            ))}
          </div>
        }
        code={`<DeadlineSetupRow\n  label={t(d.labelKey)}\n  enabled={d.on}\n  onToggle={() => toggleDeadline(d.key)}\n  date={d.date}\n  onDateChange={(v) => setDeadlineDate(d.key, v)}\n/>`}
      />
    </KitSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Documents                                                                  */
/* -------------------------------------------------------------------------- */

function DocumentsSection() {
  const dl = (
    <button type="button" className="doc-icon-btn" aria-label="Download">
      <Icon name="download" size={16} />
    </button>
  );
  return (
    <KitSection
      id="documents"
      title="Documents"
      sub="The shared file list on a trip. Each row is colour-accented by uploader — gold for Travel Connection, blue for the club — with a file-type-tinted icon and a trailing actions slot. (See Feedback for the invoice visibility tag.)"
    >
      <Spec
        name="Document row"
        tag="<DocumentRow>"
        desc="One row pattern for every document. The accent stripe + uploader chip identify who added it; the icon tile is tinted by file type (PDF red, sheet green, image blue)."
        demoClass="col start plain"
        demo={
          <Card style={{ width: "100%" }}>
            <DocumentRow
              name="Proposal — EHL KO16 Amsterdam.pdf"
              meta="PDF · 248 KB · 14 Mar 2026"
              accentColor="var(--primary)"
              fileColor={{ bg: "var(--danger-soft)", fg: "var(--danger)" }}
              uploader={{ label: "Travel Connection", isBackOffice: true }}
              actions={dl}
            />
            <DocumentRow
              name="Signed parental consent forms.pdf"
              meta="PDF · 1.4 MB · 12 Mar 2026"
              accentColor="var(--info)"
              fileColor={{ bg: "var(--danger-soft)", fg: "var(--danger)" }}
              uploader={{ label: "RC Polo", isBackOffice: false }}
              actions={
                <>
                  {dl}
                  <button type="button" className="doc-icon-btn" aria-label="Delete">
                    <Icon name="x" size={16} />
                  </button>
                </>
              }
            />
            <DocumentRow
              name="Passenger list — final.xlsx"
              meta="XLSX · 52 KB · 11 Mar 2026"
              accentColor="var(--info)"
              fileColor={{ bg: "var(--success-soft)", fg: "var(--success)" }}
              uploader={{ label: "RC Polo", isBackOffice: false }}
              actions={dl}
            />
          </Card>
        }
        code={`<DocumentRow name="Proposal.pdf" meta="PDF · 248 KB · 14 Mar 2026"\n  accentColor="var(--primary)" fileColor={{ bg: "var(--danger-soft)", fg: "var(--danger)" }}\n  uploader={{ label: "Travel Connection", isBackOffice: true }}\n  actions={<button class="doc-icon-btn">…</button>} />`}
      />
    </KitSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Page shell                                                                 */
/* -------------------------------------------------------------------------- */

export function KitPage() {
  const [active, setActive] = useState("colors");

  // Scrollspy — highlight the section nearest the top.
  useEffect(() => {
    const ids = NAV.flatMap((g) => g.links.map((l) => l.id));
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-12% 0px -70% 0px", threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="kit">
      <aside className="kit-side">
        <div className="brand">
          <span className="brand-mark">
            <LogoIcon style={{ height: 28 }} />
          </span>
          <div>
            <div className="brand-name">Travel Connection</div>
            <div className="brand-sub">Component Kit</div>
          </div>
        </div>
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="kit-navlabel">{g.group}</div>
            {g.links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                className={active === l.id ? "kit-navlink active" : "kit-navlink"}
              >
                <span className="kdot" />
                {l.label}
              </a>
            ))}
          </div>
        ))}
        <div style={{ marginTop: "auto", paddingTop: 16, fontSize: 11.5, color: "var(--text-faint)", lineHeight: 1.5 }}>
          Built on <span className="mono">globals.css</span> + <span className="mono">components/ui</span>. Live
          components — copy the markup below each.
        </div>
      </aside>

      <main className="kit-main">
        <header className="kit-hero">
          <span className="eyebrow">
            <Icon name="sparkle" size={13} /> Design reference
          </span>
          <h1>Component Kit</h1>
          <p>
            The single source of truth for how every UI element in the Travel Connection portals
            should look. Each block renders the real component, names its classes, and shows
            copy-ready markup — wire new screens to these, don't reinvent them.
          </p>
          <div className="meta-row">
            <span className="kit-chip">
              Type <span className="mono">Hanken Grotesk · Geist Mono</span>
            </span>
            <span className="kit-chip">
              Accent <span className="mono">gold · oklch(.485 .082 77)</span>
            </span>
            <span className="kit-chip">
              Radius <span className="mono">7 · 11 · 16 · 22</span>
            </span>
            <span className="kit-chip">
              Stylesheet <span className="mono">styles/globals.css</span>
            </span>
          </div>
        </header>

        <div className="kit-body">
          <KitNote>
            <b>For Claude Code:</b> import the primitives from{" "}
            <span className="mono">@/components/ui</span> and reference design tokens via CSS
            variables — never hard-code hex, px radii, or shadows. Primary actions are{" "}
            <span className="mono">--ink</span>; the gold <span className="mono">--primary</span>{" "}
            accent drives links, active nav, and focus rings.
          </KitNote>

          <ColorsSection />
          <TypeSection />
          <TokensSection />
          <IconsSection />
          <ButtonsSection />
          <BadgesSection />
          <FormsSection />
          <CardsSection />
          <DataSection />
          <FeedbackSection />
          <ProposalsSection />
          <DocumentsSection />
          <SettingsSection />
        </div>
      </main>
    </div>
  );
}
